import { MarketPriceState, UIOrderType } from '@velocity-exchange/common';
import { BigNum, OrderType, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { trimDisplayZeros } from 'src/utils/formatNumber/trimDisplayZeros';
import { TradeFormDerivedState } from 'src/@types/types';
import {
	NEW_ORDER_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING,
	OPEN_POSITION_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING,
} from 'src/constants/warnings';
import { PRICE_BUFFERS } from 'src/constants/trade';
import { VELOCITY_DOCS_URL } from 'src/constants/constants';
import { getTriggerLimitBufferPrice } from 'src/utils/slippage';
import BN from 'bn.js';
import { PositionDirection } from '@velocity-exchange/sdk';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import { getTickSizeDecimalPlaces } from 'src/hooks/useGetTickSizeForMarket';
import {
	getEffectivePriceDecimals,
	getEffectiveTickSizeInDollars,
} from 'src/constants/marketDecimalConfig';

/**
 * The limit is considered the unexpected side of the trigger if it can't fill immediately from the trigger.
 * @param currentOrderDirection
 * @param limitPrice
 * @param triggerPrice
 * @returns
 */

const getImmediatelyExecutingStopMessageProps = (
	oraclePrice: number,
	triggerPrice: number,
	orderType: UIOrderType,
	currentOrderDirection: 'long' | 'short'
) => {
	return {
		shouldShowMessage: true,
		messageTitle: 'This order will start to execute immediately',
		message: `The current oracle price is ${
			oraclePrice > triggerPrice ? 'above' : 'below'
		} the trigger oracle price.`,
		messageLinkDescription: 'Learn more',
		messageLink: `${VELOCITY_DOCS_URL}/trading/all-order-types`,
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `${orderType}_${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediateActionableStopLimitMessageProps = (
	marketPrice: number,
	limitPrice: number,
	currentOrderDirection: 'long' | 'short'
) => {
	const isLongOrder = currentOrderDirection === 'long';

	// triggered but not executed
	let messageTitle = 'This will place a limit order immediately';
	let message = `The current oracle price is ${
		isLongOrder ? 'below' : 'above'
	} the trigger oracle price.`;

	// triggered and executed
	if (
		(marketPrice <= limitPrice && currentOrderDirection === 'long') ||
		(marketPrice >= limitPrice && currentOrderDirection === 'short')
	) {
		messageTitle = 'This order will start to execute immediately';
		message = `The current market price is ${
			marketPrice > limitPrice ? 'above' : 'below'
		} the limit price.`;
	}

	return {
		shouldShowMessage: true,
		messageTitle,
		message,
		messageLinkDescription: 'Learn more',
		messageLink: `${VELOCITY_DOCS_URL}/trading/all-order-types`,
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `stopLimit_${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediatelyExecutingTakeProfitMessageProps = (
	oraclePrice: number,
	triggerPrice: number,
	orderType: OrderType,
	currentOrderDirection: 'long' | 'short'
) => {
	return {
		shouldShowMessage: true,
		messageTitle: 'This order will start to execute immediately',
		message: `The current oracle price is ${
			oraclePrice > triggerPrice ? 'above' : 'below'
		} the trigger oracle price.`,
		messageLinkDescription: 'Learn more',
		messageLink: `${VELOCITY_DOCS_URL}/trading/all-order-types`,
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediateActionableTakeProfitLimitMessageProps = (
	marketPrice: number,
	limitPrice: number,
	currentOrderDirection: 'long' | 'short'
) => {
	const isLongOrder = currentOrderDirection === 'long';

	// triggered but not executed
	let messageTitle = 'This will place a limit order immediately';
	let message = `The current oracle price is ${
		isLongOrder ? 'above' : 'below'
	} the trigger oracle price.`;

	// triggered and executed
	if (
		(marketPrice >= limitPrice && currentOrderDirection === 'long') ||
		(marketPrice <= limitPrice && currentOrderDirection === 'short')
	) {
		messageTitle = 'This order will start to execute immediately';
		message = `The current market price is ${
			marketPrice > limitPrice ? 'above' : 'below'
		} the limit price.`;
	}

	return {
		shouldShowMessage: true,
		messageTitle,
		message,
		messageLinkDescription: 'Learn more',
		messageLink: `${VELOCITY_DOCS_URL}/trading/all-order-types`,
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `takeProfitLimit_${currentOrderDirection}_immediatewarning`,
	};
};

/**
 * Check if the current market state already satisfies triggering the stop loss
 *
 * See docs
 * https://docs.velocity.exchange/order-types#D9KN6
 *
 * @param orderIsLong
 * @param oraclePrice
 * @param triggerPrice
 * @returns
 */
const isImmediateTriggeringStop = (
	orderIsLong: boolean,
	oraclePrice: number,
	triggerPrice: number
) => {
	if (orderIsLong && oraclePrice > triggerPrice) {
		// If order is STOP & LONG, then the TRIGGER CONDITION is ABOVE. So if ORACLE PRICE > TRIGGER PRICE, the order will fill immediately
		return true;
	} else if (!orderIsLong && oraclePrice < triggerPrice) {
		// If order is STOP & SHORT, then the TRIGGER CONDITION is BELOW. So if ORACLE PRICE < TRIGGER PRICE, the order will fill immediately
		return true;
	}

	return false;
};

/**
 * Check if the current market state already satisfied triggering the take profit
 *
 * See docs
 * https://docs.velocity.exchange/order-types#Bt7aa
 *
 * @param orderIsLong
 * @param oraclePrice
 * @param triggerPrice
 * @returns
 */
const isImmediateTriggeringTakeProfit = (
	orderIsLong: boolean,
	oraclePrice: number,
	triggerPrice: number
) => {
	if (orderIsLong && oraclePrice < triggerPrice) {
		// If order is TAKE PROFIT & LONG, the the TRIGGER CONDITION is BELOW. So if ORACLE PRICE < TRIGGER PRICE, then the order will fill immediately
		return true;
	} else if (!orderIsLong && oraclePrice > triggerPrice) {
		// If order is TAKE PROFIT & SHORT, the the TRIGGER CONDITION is ABOVE. So if ORACLE PRICE > TRIGGER PRICE, then the order will fill immediately
		return true;
	}

	return false;
};

const getWrongSideOfReferenceMessageProps = (
	triggerKind: 'stop' | 'takeProfit',
	orderIsLong: boolean,
	orderType: UIOrderType,
	currentOrderDirection: 'long' | 'short'
): MessageInfo => {
	const correctSide =
		triggerKind === 'stop'
			? orderIsLong
				? 'above'
				: 'below'
			: orderIsLong
				? 'below'
				: 'above';
	const priceLabel =
		triggerKind === 'stop' ? 'Stop price' : 'Take profit price';
	const title = `${priceLabel} must be ${correctSide} the reference price`;
	return {
		messageTitle: title,
		message: null,
		shouldShowMessage: true,
		messageType: 'error',
		skipMessageInConfirmationModal: false,
		messageId: `${orderType}${currentOrderDirection}_wrongsideofreference`,
		disableTradeButton: true,
	};
};

// TODO : message and messageTitle are "required" , but aren't both being used in the TpSlOrderFormPopup ?? Makes no sense
export interface MessageInfo extends Pick<
	TradeFormDerivedState,
	| 'messageType'
	| 'shouldShowMessage'
	| 'message'
	| 'skipMessageInConfirmationModal'
	| 'messageLink'
	| 'messageLinkDescription'
	| 'messageTitle'
	| 'messageId'
	| 'messagePosition'
> {
	disableTradeButton?: boolean;
}

const MARK_PRICE_WARNING_BPS_THRESHOLD = 0.0001;

/**
 * Checks for order violations
 * @param orderType
 * @param isSpot
 * @param priceBoxValue
 * @param orderDirection
 * @param marketPrice
 * @param postOnly
 * @param userIsShort
 * @param baseSize
 * @param secondaryPriceboxValue
 * @param currentPositionDirection
 * @returns
 */
export const getMessageInfoForOrderViolation = (
	orderType: UIOrderType,
	priceBoxValue: number,
	marketPrice: number,
	postOnly: boolean,
	secondaryPriceboxValue: number,
	currentOrderDirection: 'long' | 'short',
	oraclePrice: number,
	marketSymbol: string,
	baseSize?: number,
	stepSize?: number,
	source?: 'tradeForm' | 'closePosition' | 'editOrder',
	referencePrice?: number,
	marketPriceState?: MarketPriceState | null
): MessageInfo | undefined => {
	const orderIsLong = currentOrderDirection === 'long';

	if (
		marketSymbol &&
		baseSize &&
		stepSize &&
		baseSize > 0 &&
		stepSize > 0 &&
		baseSize < stepSize &&
		source !== 'tradeForm' // tradeform has its own size input validation
	) {
		return {
			message: `Order size must be greater than ${stepSize} ${marketSymbol}`,
			shouldShowMessage: true,
			messageTitle: `Order size must be greater than ${stepSize} ${marketSymbol}`,
			messageType: 'error',
			skipMessageInConfirmationModal: false,
			messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
			disableTradeButton: true,
		};
	}

	// A zero/empty price is not a valid basis for order violation warnings
	if (!priceBoxValue) {
		return undefined;
	}

	switch (orderType) {
		case 'limit': {
			const limitPrice = priceBoxValue;

			const messageTitle = postOnly
				? 'This post only order may fail'
				: 'This order will start to execute immediately';

			if (
				willCrossOrderbook(
					marketPrice,
					limitPrice,
					orderIsLong,
					marketPriceState
				)
			) {
				return {
					messageTitle,
					shouldShowMessage: true,
					message: orderIsLong
						? `The limit price is above or equal to the current orderbook ask price`
						: `The limit price is below or equal to the current orderbook bid price`,
					messageType: 'warning',
					skipMessageInConfirmationModal: false,
					messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					disableTradeButton: postOnly,
				};
			}
			break;
		}
		case 'stopMarket': {
			const triggerPrice = priceBoxValue;

			if (
				referencePrice &&
				isImmediateTriggeringStop(orderIsLong, referencePrice, triggerPrice)
			) {
				return getWrongSideOfReferenceMessageProps(
					'stop',
					orderIsLong,
					orderType,
					currentOrderDirection
				);
			}

			if (isImmediateTriggeringStop(orderIsLong, marketPrice, triggerPrice)) {
				return getImmediatelyExecutingStopMessageProps(
					oraclePrice,
					triggerPrice,
					orderType,
					currentOrderDirection
				);
			}
			break;
		}
		case 'stopLimit': {
			const triggerPrice = priceBoxValue;
			const limitPrice = secondaryPriceboxValue;

			if (
				referencePrice &&
				isImmediateTriggeringStop(orderIsLong, referencePrice, triggerPrice)
			) {
				return getWrongSideOfReferenceMessageProps(
					'stop',
					orderIsLong,
					orderType,
					currentOrderDirection
				);
			}

			if (isImmediateTriggeringStop(orderIsLong, oraclePrice, triggerPrice)) {
				return getImmediateActionableStopLimitMessageProps(
					marketPrice,
					limitPrice,
					currentOrderDirection
				);
			}

			break;
		}
		case 'takeProfitMarket': {
			const triggerPrice = priceBoxValue;

			if (
				referencePrice &&
				isImmediateTriggeringTakeProfit(
					orderIsLong,
					referencePrice,
					triggerPrice
				)
			) {
				return getWrongSideOfReferenceMessageProps(
					'takeProfit',
					orderIsLong,
					orderType,
					currentOrderDirection
				);
			}

			if (
				isImmediateTriggeringTakeProfit(orderIsLong, oraclePrice, triggerPrice)
			) {
				return getImmediatelyExecutingTakeProfitMessageProps(
					oraclePrice,
					triggerPrice,
					orderType,
					currentOrderDirection
				);
			}
			break;
		}
		case 'takeProfitLimit': {
			const triggerPrice = priceBoxValue;
			const limitPrice = secondaryPriceboxValue;

			if (
				referencePrice &&
				isImmediateTriggeringTakeProfit(
					orderIsLong,
					referencePrice,
					triggerPrice
				)
			) {
				return getWrongSideOfReferenceMessageProps(
					'takeProfit',
					orderIsLong,
					orderType,
					currentOrderDirection
				);
			}

			if (
				isImmediateTriggeringTakeProfit(orderIsLong, oraclePrice, triggerPrice)
			) {
				return getImmediateActionableTakeProfitLimitMessageProps(
					marketPrice,
					limitPrice,
					currentOrderDirection
				);
			}

			break;
		}
		case 'oracleLimit': {
			const offset = priceBoxValue;
			const limitPrice = oraclePrice + offset;

			const messageTitle = postOnly
				? 'This post only order may fail'
				: 'This order will start to execute immediately';

			if (
				willCrossOrderbook(
					marketPrice,
					limitPrice,
					orderIsLong,
					marketPriceState
				)
			) {
				return {
					messageTitle,
					shouldShowMessage: true,
					message: orderIsLong
						? `The limit price is above or equal to the current orderbook ask price`
						: `The limit price is below or equal to the current orderbook bid price`,
					messageType: postOnly ? 'warning' : 'info',
					skipMessageInConfirmationModal: false,
					messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
				};
			}
			break;
		}
		case 'scaledOrders': {
			const startPrice = priceBoxValue;
			const endPrice = secondaryPriceboxValue;

			const messageTitle = postOnly
				? 'Some post only orders will fail'
				: 'Some orders will start to execute immediately';

			// The most aggressive end of the ladder is the one that can cross.
			const limitPrice = orderIsLong
				? Math.max(startPrice, endPrice)
				: Math.min(startPrice, endPrice);

			if (
				willCrossOrderbook(
					marketPrice,
					limitPrice,
					orderIsLong,
					marketPriceState
				)
			) {
				return {
					messageTitle,
					shouldShowMessage: true,
					message: orderIsLong
						? `The limit price is above or equal to the current orderbook ask price`
						: `The limit price is below or equal to the current orderbook bid price`,
					messageType: 'warning',
					skipMessageInConfirmationModal: false,
					messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					disableTradeButton: postOnly,
				};
			}
			break;
		}
		default:
			break;
	}
};

export const getPriceInputLabel = (orderType: UIOrderType) => {
	switch (orderType) {
		case 'market':
			return '';
		case 'limit':
			return 'Price';
		case 'stopLimit':
		case 'stopMarket':
			return 'Stop Price';
		case 'takeProfitMarket':
		case 'takeProfitLimit':
			return 'Trigger Price';
		case 'oracle':
			return 'Oracle Market';
		case 'oracleLimit':
			return 'Price Offset';
		case 'scaledOrders':
			return 'Start Price';
		default:
			console.warn(`getPriceInputLabel: Unhandled order type: ${orderType}`);
			return '';
	}
};

export function orderTypeSwitch<T = UIOrderType>(
	orderType: T,
	cases: {
		//@ts-ignore
		[key in T]: any;
	}
) {
	return cases[orderType];
}

export const getShouldShowSecondaryPriceBox = (orderType: UIOrderType) => {
	switch (orderType) {
		case 'market':
		case 'stopMarket':
		case 'takeProfitMarket':
		case 'oracleLimit':
		case 'oracle':
		case 'limit':
			return false;
		default:
			return true;
	}
};

/**
 * Checks if a limit price is too close to a trigger price for a trigger order.
 * Prices should be passed in at trade precision
 * Returns a warning message if the prices are too close, null otherwise.
 */
export const getLimitPriceVsTriggerPriceWarning = (
	triggerPrice: BN | undefined,
	limitPrice: BN | undefined,
	direction: 'long' | 'short',
	isExistingOrder: boolean
): string | null => {
	if (!triggerPrice || !limitPrice) {
		return null;
	}

	const bufferLimitPrice = getTriggerLimitBufferPrice(
		BigNum.from(triggerPrice, PRICE_PRECISION_EXP),
		direction === 'long' ? PositionDirection.LONG : PositionDirection.SHORT,
		PRICE_BUFFERS.TRIGGER_WARNING_PCT // we use a 0.99% buffer instead of a 1% buffer, because we use 1% for deriving the default limit price from the trigger price. This avoid unnecessary warning caused by the default 1% buffer
	);

	if (bufferLimitPrice.eqZero()) {
		return null;
	}

	const isPastBuffer =
		direction === 'long'
			? limitPrice.lt(bufferLimitPrice.val) && limitPrice.gt(triggerPrice)
			: limitPrice.gt(bufferLimitPrice.val) && limitPrice.lt(triggerPrice);

	if (isPastBuffer) {
		if (isExistingOrder) {
			return OPEN_POSITION_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING;
		}
		return NEW_ORDER_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING;
	}

	return null;
};

/**
 * The fraction digits the price field's mask admits: the on-chain tick
 * intersected with the market's display config, which is the tighter of the two
 * and can sit well below the tick. A zero tick has no digits to read, and
 * without a tick or a symbol there is no lattice to hold a price to at all.
 */
const priceBoxFractionDigits = (
	tickSize: BigNum | undefined,
	marketSymbol: string | undefined
) =>
	tickSize && !tickSize.eqZero() && marketSymbol
		? getEffectivePriceDecimals(
				marketSymbol,
				getTickSizeDecimalPlaces(tickSize.val)
			)
		: undefined;

/**
 * Renders a price the code sets rather than the user types, at the digits the
 * mask admits and moving away from the price it was measured from. Truncating
 * would move it back towards that price, and a buffer worth less than one
 * displayed digit would land on it exactly.
 */
const priceBoxText = (
	value: number,
	fractionDigits: number | undefined,
	away: 'up' | 'down'
) =>
	fractionDigits === undefined
		? formatText(value, PRESETS.priceText)
		: formatText(value, {
				...PRESETS.priceText,
				digits: {
					kind: 'decimals',
					decimals: fractionDigits,
					rounding: away === 'up' ? 'ceil' : 'floor',
				},
			});

/**
 * Updates price boxes with appropriate buffers based on order type and direction.
 * Extracted from useTradeFormStateEngine for reuse across components.
 */
export const updatePriceBoxesWithBuffers = (
	defaultPrice: BigNum | undefined,
	currentOrderType: UIOrderType,
	prevOrderType: UIOrderType | undefined,
	direction: 'long' | 'short',
	priceBoxValue: string,
	secondaryPriceBoxValue: string,
	primaryPriceBoxUpdate: (value: string) => void,
	secondaryPriceBoxUpdate: (value: string) => void,
	tickSize?: BigNum,
	marketSymbol?: string,
	prevMarketSymbol?: string
) => {
	if (!defaultPrice) return;

	// When switching to order types besides oracle limit & scaled, we skip
	// updating prices if they've already been set. Main exceptions include:
	//   - We're switching FROM oracle limit, in which case we DO update the
	// price box so that the offset price value, which doesn't make sense for
	// other order types, doesn't remain.
	//   - We're switching markets, as the prev market prices are irrelevant.
	const switchedMarket = prevMarketSymbol && prevMarketSymbol !== marketSymbol;
	const shouldUpdatePriceBox =
		!priceBoxValue ||
		(prevOrderType === 'oracleLimit' && currentOrderType !== 'oracleLimit') ||
		switchedMarket;
	const shouldUpdateSecondaryPriceBox =
		!secondaryPriceBoxValue || switchedMarket;

	switch (currentOrderType) {
		case 'limit': {
			if (!shouldUpdatePriceBox) {
				break;
			}

			// Add 1% buffer to default price to avoid fluctuating oracle price
			// Buy orders: price - 1% (lower than mark price)
			// Sell orders: price + 1% (higher than mark price)
			const limitPriceToUse = defaultPrice;

			const limitPriceWithBuffer =
				direction === 'long'
					? limitPriceToUse.toNum() * (1 - PRICE_BUFFERS.DEFAULT_FRACTION) // Buy: reduce price by 1_FRACTION%
					: limitPriceToUse.toNum() * (1 + PRICE_BUFFERS.DEFAULT_FRACTION); // Sell: increase price by 1_FRACTION%

			const tickSizeDecimalPlace = tickSize
				? getTickSizeDecimalPlaces(tickSize.val)
				: 0;

			const effectiveDecimalPlaces = marketSymbol
				? getEffectivePriceDecimals(marketSymbol, tickSizeDecimalPlace)
				: tickSizeDecimalPlace;

			primaryPriceBoxUpdate(
				trimDisplayZeros(
					limitPriceWithBuffer.toFixed(effectiveDecimalPlaces),
					0
				)
			);
			break;
		}
		case 'oracleLimit': {
			if (!tickSize || !marketSymbol) {
				primaryPriceBoxUpdate(
					tickSize
						? direction === 'short'
							? tickSize.prettyPrint()
							: tickSize.neg().prettyPrint()
						: ''
				);
			} else {
				// Calculate effective tick size in dollars based on market configuration
				const tickSizeInDollars = tickSize.toNum();
				const effectiveTickSizeInDollars = getEffectiveTickSizeInDollars(
					marketSymbol,
					tickSizeInDollars
				);

				const effectiveTickSizeValue =
					direction === 'short'
						? effectiveTickSizeInDollars
						: -effectiveTickSizeInDollars;

				const tickSizeDecimalPlace = getTickSizeDecimalPlaces(tickSize.val);
				const effectiveDecimalPlaces = getEffectivePriceDecimals(
					marketSymbol,
					tickSizeDecimalPlace
				);

				primaryPriceBoxUpdate(
					trimDisplayZeros(
						effectiveTickSizeValue.toFixed(effectiveDecimalPlaces),
						0
					)
				);
			}
			break;
		}
		case 'stopMarket':
		case 'takeProfitMarket':
		case 'stopLimit':
		case 'takeProfitLimit': {
			const useHigherPrice =
				(direction === 'short' &&
					(currentOrderType == 'takeProfitMarket' ||
						currentOrderType == 'takeProfitLimit')) ||
				(direction === 'long' &&
					(currentOrderType == 'stopMarket' ||
						currentOrderType == 'stopLimit'));

			const isAdvancedLimit =
				currentOrderType == 'stopLimit' ||
				currentOrderType == 'takeProfitLimit';

			let primaryPriceBoxVal = Number(defaultPrice.toTradePrecision());
			let secondaryPriceBoxVal = Number(defaultPrice.toTradePrecision());

			// Apply 1% buffer to trigger orders to avoid immediate execution
			const isTriggerOrder =
				currentOrderType == 'stopMarket' ||
				currentOrderType == 'stopLimit' ||
				currentOrderType == 'takeProfitMarket' ||
				currentOrderType == 'takeProfitLimit';

			if (isTriggerOrder) {
				if (useHigherPrice) {
					primaryPriceBoxVal =
						primaryPriceBoxVal * (1 + PRICE_BUFFERS.DEFAULT_FRACTION);
				} else {
					primaryPriceBoxVal =
						primaryPriceBoxVal * (1 - PRICE_BUFFERS.DEFAULT_FRACTION);
				}
			}

			if (
				currentOrderType === 'stopLimit' ||
				currentOrderType === 'takeProfitLimit'
			) {
				secondaryPriceBoxVal = getTriggerLimitBufferPrice(
					BigNum.fromPrint(primaryPriceBoxVal.toString(), PRICE_PRECISION_EXP),
					direction === 'long'
						? PositionDirection.LONG
						: PositionDirection.SHORT
				).toNum();
			}

			const fractionDigits = priceBoxFractionDigits(tickSize, marketSymbol);

			if (shouldUpdatePriceBox) {
				primaryPriceBoxUpdate(
					primaryPriceBoxVal
						? priceBoxText(
								primaryPriceBoxVal,
								fractionDigits,
								useHigherPrice ? 'up' : 'down'
							)
						: ''
				);
			}

			if (isAdvancedLimit && shouldUpdateSecondaryPriceBox) {
				// The limit sits above the trigger for a long and below it for a
				// short, so it moves away from the trigger on the same side.
				secondaryPriceBoxUpdate(
					secondaryPriceBoxVal
						? priceBoxText(
								secondaryPriceBoxVal,
								fractionDigits,
								direction === 'long' ? 'up' : 'down'
							)
						: ''
				);
			}

			break;
		}

		case 'scaledOrders': {
			let primaryPriceBoxVal = Number(defaultPrice.toTradePrecision());
			let secondaryPriceBoxVal = Number(defaultPrice.toTradePrecision());

			if (direction === 'short') {
				primaryPriceBoxVal =
					primaryPriceBoxVal * (1 + PRICE_BUFFERS.DEFAULT_FRACTION);
				secondaryPriceBoxVal =
					secondaryPriceBoxVal * (1 + PRICE_BUFFERS.SCALED_ORDERS_FRACTION);
			} else {
				primaryPriceBoxVal =
					primaryPriceBoxVal * (1 - PRICE_BUFFERS.DEFAULT_FRACTION);
				secondaryPriceBoxVal =
					secondaryPriceBoxVal * (1 - PRICE_BUFFERS.SCALED_ORDERS_FRACTION);
			}

			const fractionDigits = priceBoxFractionDigits(tickSize, marketSymbol);
			// Both ends of the ladder are buffered away from the mark on the same
			// side, so both round away from it too.
			const away = direction === 'short' ? 'up' : 'down';

			primaryPriceBoxUpdate(
				primaryPriceBoxVal
					? priceBoxText(primaryPriceBoxVal, fractionDigits, away)
					: ''
			);
			secondaryPriceBoxUpdate(
				secondaryPriceBoxVal
					? priceBoxText(secondaryPriceBoxVal, fractionDigits, away)
					: ''
			);

			break;
		}

		case 'market':
		case 'oracle':
			if (shouldUpdatePriceBox) {
				primaryPriceBoxUpdate(defaultPrice.toNum().toString());
			}

			break;
		default: {
			const exhaustiveCheck: never = currentOrderType;
			console.warn(
				`updatePriceBoxesWithBuffers: Unhandled order type: ${exhaustiveCheck}`
			);
			break;
		}
	}
};

const priceAboveMarkPriceThreshold = (
	markPrice: number,
	limitPrice: number,
	isLong: boolean
) => {
	if (isLong) {
		return limitPrice >= markPrice * (1 + MARK_PRICE_WARNING_BPS_THRESHOLD);
	}
	return limitPrice <= markPrice * (1 - MARK_PRICE_WARNING_BPS_THRESHOLD);
};

/**
 * Top of the side a limit order would cross: buys lift the best ask, sells hit
 * the best bid. Uses the ungrouped top of book, so the result doesn't shift with
 * the orderbook's display grouping.
 */
const getCrossingPrice = (
	marketPriceState: MarketPriceState | null | undefined,
	isLong: boolean
): number | undefined => {
	const best = isLong ? marketPriceState?.bestAsk : marketPriceState?.bestBid;
	const price = best?.toNum();

	return price && price > 0 ? price : undefined;
};

/**
 * A limit order only executes on placement if it crosses the opposite side of
 * the book. Comparing against the mark price instead flags every price resting
 * inside the spread, which is wrong whenever the spread is wider than a basis
 * point. The mark price is only a fallback for when the book isn't loaded yet.
 */
const willCrossOrderbook = (
	markPrice: number,
	limitPrice: number,
	isLong: boolean,
	marketPriceState?: MarketPriceState | null
) => {
	const crossingPrice = getCrossingPrice(marketPriceState, isLong);

	if (crossingPrice === undefined) {
		return priceAboveMarkPriceThreshold(markPrice, limitPrice, isLong);
	}

	return isLong ? limitPrice >= crossingPrice : limitPrice <= crossingPrice;
};
